import { Command } from 'commander'
import { createApiClient } from '../http.js'
import { settings } from '../api/catalog.js'
import { call } from '../api/client.js'
import { resolveAccountId, resolveWorkspaceId } from '../context.js'
import { printJson } from '../output.js'

interface SettingFlags {
  account?: string
  workspace?: string
  json?: boolean
}

/** Settings attach to an Account or Workspace parent; workspace wins when both are present. */
async function settingScope(flags: SettingFlags): Promise<{ scopeKind: string; parentId: string }> {
  const workspaceId = await resolveWorkspaceId(flags, false)
  if (flags.workspace || (!flags.account && workspaceId)) {
    if (!workspaceId) throw new Error('No workspace selected.')
    return { scopeKind: 'Workspace', parentId: workspaceId }
  }
  const accountId = (await resolveAccountId(flags))!
  return { scopeKind: 'Account', parentId: accountId }
}

export function registerSettings(program: Command): void {
  const settingsCmd = program.command('settings').description('Manage platform settings (account or workspace scope)')

  settingsCmd
    .command('list')
    .description('List settings in the selected scope')
    .option('--account <accountId>', 'account scope')
    .option('--workspace <workspaceId>', 'workspace scope')
    .option('--secrets', 'include secret values')
    .action(async (options: SettingFlags & { secrets?: boolean }) => {
      const api = await createApiClient()
      const scope = await settingScope(options)
      const result = await call<unknown>(api, settings.list, {
        query: { ...scope, includeSecretValues: options.secrets ?? false },
      })
      printJson(result)
    })

  settingsCmd
    .command('get <key>')
    .description('Read one setting')
    .option('--account <accountId>')
    .option('--workspace <workspaceId>')
    .option('--secrets', 'include secret values')
    .action(async (key: string, options: SettingFlags & { secrets?: boolean }) => {
      const api = await createApiClient()
      const scope = await settingScope(options)
      const result = await call<unknown>(api, settings.get, {
        params: { key },
        query: { ...scope, includeSecretValues: options.secrets ?? false },
      })
      printJson(result)
    })

  settingsCmd
    .command('set <key> <value>')
    .description('Create or update a setting (value parsed as JSON when possible)')
    .option('--account <accountId>')
    .option('--workspace <workspaceId>')
    .action(async (key: string, value: string, options: SettingFlags) => {
      const api = await createApiClient()
      const scope = await settingScope(options)
      let parsed: unknown = value
      try {
        parsed = JSON.parse(value)
      } catch {
        // keep as string
      }
      await call(api, settings.upsert, {
        params: { key },
        query: scope,
        body: { scopeKind: scope.scopeKind, parentId: scope.parentId, key, value: parsed },
      })
      console.log(`Setting ${key} saved (${scope.scopeKind.toLowerCase()} scope).`)
    })

  settingsCmd
    .command('delete <key>')
    .description('Delete a setting')
    .option('--account <accountId>')
    .option('--workspace <workspaceId>')
    .action(async (key: string, options: SettingFlags) => {
      const api = await createApiClient()
      const scope = await settingScope(options)
      await call(api, settings.delete, { params: { key }, query: scope })
      console.log(`Setting ${key} deleted.`)
    })
}
